import React from 'react';
import styled from '@emotion/styled';
import MuiCheckIcon from '@mui/icons-material/Check';
import { Card } from '../Card';
import CardBadge from '../Cards/CardBadge';
import Button from '../Button';
import Headline3 from '../Typography/Heading/Headline3';
import Body1 from '../Typography/Body/Body1';
import { CheckItem } from '../FeatureCheckList';
import breakpoints from '../Theme/breakpoints';
import colors from '../Theme/colors';
import grey from '../Palettes/grey';
import rose from '../Palettes/rose';
import Headline4 from '../Typography/Heading/Headline4';
import Subtitle from '../Typography/SubTitle';

export interface PackageFeature {
  key: string;
  label: string;
  absent?: boolean;
}

export interface PackageTier {
  id: string;
  title: string;
  tagline: string;
  features: PackageFeature[];
  ctaLabel: string;
  flagship?: boolean;
  flagshipBadgeLabel?: string;
}

interface PackageTierComparisonProperties {
  headingMessage?: string;
  ctaHref: string;
  tiers: PackageTier[];
}

const StyledHeadline3 = styled(Headline3)`
  && {
    text-align: center;
    margin-bottom: 1.5rem;
  }
`;

const TierGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  margin: 2rem 0;

  @media (min-width: ${breakpoints.md}px) {
    grid-template-columns: repeat(2, 1fr);
    align-items: stretch;
  }

  @media (min-width: ${breakpoints.lg}px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const TierCard = styled(Card, {
  shouldForwardProp: (property) => property !== 'flagship',
})<{ flagship?: boolean }>`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: visible;
  background-color: ${colors.white};
  border: ${({ flagship }) => (flagship ? '2px' : '1px')} solid
    ${({ flagship }) => (flagship ? rose[600] : grey[300])};
  box-shadow: ${({ flagship }) => (
    flagship ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none'
  )};
`;

const FlagshipBadge = styled(CardBadge)`
  background-color: ${rose[600]};
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const TierCardContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const TierEyebrow = styled(Subtitle)`
  color: ${rose[600]};
  text-transform: uppercase;
`;

const TierTagline = styled(Headline4)`
  margin: 0 0 1rem;
`;

const FeatureList = styled.div`
  margin: 0 0 24px;
`;

const FeatureItem = styled(CheckItem)`
  align-items: center;
`;

const FeatureIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${rose[100]};
  color: ${rose[600]};

  svg {
    font-size: 0.875rem;
  }
`;

const AbsentIconWrapper = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px dashed ${grey[400]};
`;

const AbsentLabel = styled(Body1)`
  && {
    color: ${grey[500]};
  }
`;

const CtaContainer = styled.div`
  margin-top: auto;

  & button {
    text-align: center;
    justify-content: center;
  }
`;

interface TierColumnProperties {
  tier: PackageTier;
  ctaHref: string;
}

const TierColumn = ({ tier, ctaHref }: TierColumnProperties) => {
  const {
    title,
    tagline,
    features,
    ctaLabel,
    flagship,
    flagshipBadgeLabel,
  } = tier;

  return (
    <TierCard flagship={flagship}>
      {flagship && flagshipBadgeLabel && (
        <FlagshipBadge>{flagshipBadgeLabel}</FlagshipBadge>
      )}
      <TierCardContent>
        <TierEyebrow>{title}</TierEyebrow>
        <TierTagline>{tagline}</TierTagline>
        <FeatureList>
          {features.map(({ key, label, absent }) => (
            <FeatureItem key={key}>
              {absent ? (
                <>
                  <AbsentIconWrapper />
                  <AbsentLabel>{label}</AbsentLabel>
                </>
              ) : (
                <>
                  <FeatureIconWrapper>
                    <MuiCheckIcon />
                  </FeatureIconWrapper>
                  <Body1>{label}</Body1>
                </>
              )}
            </FeatureItem>
          ))}
        </FeatureList>
        <CtaContainer>
          <Button
            href={ctaHref}
            variant={flagship ? 'contained' : 'outlined'}
            fullWidth
          >
            {ctaLabel}
          </Button>
        </CtaContainer>
      </TierCardContent>
    </TierCard>
  );
};

const PackageTierComparison = ({
  headingMessage,
  ctaHref,
  tiers,
}: PackageTierComparisonProperties) => (
  <>
    {headingMessage && (
      <StyledHeadline3>{headingMessage}</StyledHeadline3>
    )}
    <TierGrid>
      {tiers.map((tier) => (
        <TierColumn key={tier.id} tier={tier} ctaHref={ctaHref} />
      ))}
    </TierGrid>
  </>
);

export default PackageTierComparison;
